import { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import type { Card, Etapa } from '../../types';

interface ModalAdicionarFilhoProps {
  cardPai: Card;
  cards: Card[];
  etapas: Etapa[];
  onAdicionarFilho: (cardFilho: Card) => void;
  onClose: () => void;
}

const ModalAdicionarFilho = ({
  cardPai,
  cards,
  etapas,
  onAdicionarFilho,
  onClose
}: ModalAdicionarFilhoProps) => {
  const [busca, setBusca] = useState('');
  const [etapaSelecionada, setEtapaSelecionada] = useState<string>('todas');

  // Cards disponíveis (não é o próprio pai, não é card pai, não tem pai)
  const cardsDisponiveis = useMemo(() => {
    return cards.filter(c => 
      String(c.id) !== String(cardPai.id) &&
      !c.ehCardPai &&
      !c.cardPaiId
    );
  }, [cards, cardPai.id]);

  // Etapas que têm cards disponíveis
  const etapasComCards = useMemo(() => {
    const etapaIds = new Set(cardsDisponiveis.map(c => String(c.etapaId)));
    return etapas.filter(e => etapaIds.has(String(e.id)));
  }, [cardsDisponiveis, etapas]);

  // Cards filtrados por etapa e busca
  const cardsFiltrados = useMemo(() => {
    return cardsDisponiveis.filter(card => {
      // Filtro por etapa
      if (etapaSelecionada !== 'todas' && String(card.etapaId) !== etapaSelecionada) {
        return false;
      }
      
      // Filtro por busca (título)
      if (busca.trim()) {
        const termoBusca = busca.toLowerCase().trim();
        const tituloMatch = (card.titulo || '').toLowerCase().includes(termoBusca);

        // Também buscar nos valores dos campos
        let camposMatch = false;
        try {
          camposMatch = Object.values(card.campos || {}).some(valor => {
            if (typeof valor === 'string') {
              return valor.toLowerCase().includes(termoBusca);
            }
            if (Array.isArray(valor)) {
              return valor.some(v => typeof v === 'string' && v.toLowerCase().includes(termoBusca));
            }
            return false;
          });
        } catch {
          camposMatch = false;
        }

        return tituloMatch || camposMatch;
      }
      
      return true;
    });
  }, [cardsDisponiveis, etapaSelecionada, busca]);

  // Contagem por etapa
  const contagemPorEtapa = useMemo(() => {
    const contagem: Record<string, number> = {};
    cardsDisponiveis.forEach(card => {
      const etapaId = String(card.etapaId);
      contagem[etapaId] = (contagem[etapaId] || 0) + 1;
    });
    return contagem;
  }, [cardsDisponiveis]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-600 to-indigo-600 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-xl">🎴</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  Adicionar Filho
                </h2>
                <p className="text-sm text-white/80 truncate max-w-[350px]">
                  Card Pai: {cardPai.titulo}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Filtros */}
        <div className="p-4 bg-gray-50 border-b border-gray-100 space-y-3 flex-shrink-0">
          {/* Campo de busca */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por título ou conteúdo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              autoFocus
            />
            {busca && (
              <button
                onClick={() => setBusca('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filtro por etapa */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setEtapaSelecionada('todas')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  etapaSelecionada === 'todas'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Todas ({cardsDisponiveis.length})
              </button>
              {etapasComCards.map(etapa => (
                <button
                  key={etapa.id}
                  onClick={() => setEtapaSelecionada(String(etapa.id))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    etapaSelecionada === String(etapa.id)
                      ? 'bg-purple-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {etapa.nome} ({contagemPorEtapa[String(etapa.id)] || 0})
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Resultado da busca */}
        {(busca || etapaSelecionada !== 'todas') && (
          <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex-shrink-0">
            <p className="text-xs text-purple-700">
              {cardsFiltrados.length} {cardsFiltrados.length === 1 ? 'card encontrado' : 'cards encontrados'}
              {busca && <span className="font-medium"> para "{busca}"</span>}
              {etapaSelecionada !== 'todas' && (
                <span className="font-medium"> na etapa {etapasComCards.find(e => String(e.id) === etapaSelecionada)?.nome}</span>
              )}
            </p>
          </div>
        )}
        
        {/* Lista de cards */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {cardsDisponiveis.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📭</span>
              </div>
              <p className="text-gray-500 font-medium">Nenhum card disponível</p>
              <p className="text-xs text-gray-400 mt-1">Todos os cards já são pais ou filhos</p>
            </div>
          ) : cardsFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <p className="text-gray-500 font-medium">Nenhum resultado</p>
              <p className="text-xs text-gray-400 mt-1">Tente ajustar os filtros</p>
              <button
                onClick={() => { setBusca(''); setEtapaSelecionada('todas'); }}
                className="mt-3 text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Limpar filtros
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {cardsFiltrados.map(card => {
                const etapaCard = etapas.find(e => String(e.id) === String(card.etapaId));
                return (
                  <button
                    key={card.id}
                    onClick={() => onAdicionarFilho(card)}
                    className="w-full text-left p-3 border border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                        <span className="text-indigo-600 text-lg group-hover:text-purple-700">→</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-sm truncate">{card.titulo || 'Sem título'}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          <span 
                            className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400"
                          ></span>
                          <span className="truncate">{etapaCard?.nome || 'Etapa desconhecida'}</span>
                        </div>
                      </div>
                      <div className="text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors font-medium text-sm"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalAdicionarFilho;
